/**
 * CEO Command Center - AI Agent Tools
 *
 * Defines the tools/functions that the AI Agent can invoke
 * to modify data in the system.
 *
 * @module services/ai/tools
 * @version 11.0.0
 */

// ============================================
// TOOL PARAMETER TYPES
// ============================================

export interface ToolParameter {
  type: 'string' | 'number' | 'boolean';
  description: string;
  enum?: string[];
  required?: boolean;
}

export interface AITool {
  name: string;
  description: string;
  category: 'finance' | 'sales' | 'tasks' | 'clients' | 'investors';
  parameters: Record<string, ToolParameter>;
  requiresConfirmation?: boolean;
}

// ============================================
// FINANCE TOOLS
// ============================================

const financeTools: AITool[] = [
  {
    name: 'update_cash_position',
    description: 'Update the cash balance for a specific month. Use this when the user wants to change the bank balance or cash position.',
    category: 'finance',
    requiresConfirmation: true,
    parameters: {
      month: {
        type: 'string',
        description: 'Month identifier (e.g., "Jan 2026", "Feb 2025")',
        required: true,
      },
      balance: {
        type: 'number',
        description: 'New cash balance in EUR',
        required: true,
      },
    },
  },
  {
    name: 'update_cashflow_revenue',
    description: 'Update revenue fields (recurring revenue or new deal revenue) for a specific month.',
    category: 'finance',
    requiresConfirmation: true,
    parameters: {
      month: {
        type: 'string',
        description: 'Month identifier (e.g., "Jan 2026")',
        required: true,
      },
      field: {
        type: 'string',
        description: 'Revenue field to update',
        enum: ['recurringRevenue', 'newDealRevenue'],
        required: true,
      },
      value: {
        type: 'number',
        description: 'New value in EUR',
        required: true,
      },
    },
  },
  {
    name: 'update_cashflow_expense',
    description: 'Update expense/cost fields for a specific month (personnel, infrastructure, marketing, legal, other).',
    category: 'finance',
    requiresConfirmation: true,
    parameters: {
      month: {
        type: 'string',
        description: 'Month identifier (e.g., "Jan 2026")',
        required: true,
      },
      field: {
        type: 'string',
        description: 'Expense field to update',
        enum: ['personnelCost', 'infrastructureCost', 'marketingCost', 'legalCost', 'otherCost'],
        required: true,
      },
      value: {
        type: 'number',
        description: 'New value in EUR',
        required: true,
      },
    },
  },
  {
    name: 'create_cashflow_record',
    description: 'Create a new cash flow record for a month that does not exist yet.',
    category: 'finance',
    requiresConfirmation: true,
    parameters: {
      month: {
        type: 'string',
        description: 'Month identifier (e.g., "Dec 2026")',
        required: true,
      },
      recurringRevenue: {
        type: 'number',
        description: 'Recurring revenue in EUR',
        required: false,
      },
      newDealRevenue: {
        type: 'number',
        description: 'New deal revenue in EUR',
        required: false,
      },
      personnelCost: {
        type: 'number',
        description: 'Personnel costs in EUR',
        required: false,
      },
      balance: {
        type: 'number',
        description: 'Cash balance at end of month in EUR',
        required: false,
      },
      isForecast: {
        type: 'boolean',
        description: 'Whether this is a forecast (true) or actual (false)',
        required: false,
      },
    },
  },
];

// ============================================
// SALES/DEAL TOOLS
// ============================================

const salesTools: AITool[] = [
  {
    name: 'create_deal',
    description: 'Create a new sales deal/opportunity in the pipeline.',
    category: 'sales',
    requiresConfirmation: true,
    parameters: {
      prospect: {
        type: 'string',
        description: 'Company/prospect name',
        required: true,
      },
      tcv: {
        type: 'number',
        description: 'Total Contract Value in EUR',
        required: true,
      },
      probability: {
        type: 'number',
        description: 'Win probability (0-100 or 0-1)',
        required: false,
      },
      stage: {
        type: 'string',
        description: 'Sales stage',
        enum: ['Qualification', 'Discovery', 'Proposal', 'Negotiation', 'Verbal Commit', 'Closed Won', 'Closed Lost'],
        required: false,
      },
      owner: {
        type: 'string',
        description: 'Sales owner name',
        required: false,
      },
      closeDate: {
        type: 'string',
        description: 'Expected close date (YYYY-MM-DD)',
        required: false,
      },
    },
  },
  {
    name: 'update_deal',
    description: 'Update an existing deal. Use this to change deal value, probability, stage, or other fields.',
    category: 'sales',
    requiresConfirmation: true,
    parameters: {
      prospect: {
        type: 'string',
        description: 'Company/prospect name to identify the deal',
        required: true,
      },
      tcv: {
        type: 'number',
        description: 'New Total Contract Value in EUR',
        required: false,
      },
      probability: {
        type: 'number',
        description: 'New win probability (0-100 or 0-1)',
        required: false,
      },
      stage: {
        type: 'string',
        description: 'New sales stage',
        enum: ['Qualification', 'Discovery', 'Proposal', 'Negotiation', 'Verbal Commit', 'Closed Won', 'Closed Lost'],
        required: false,
      },
      owner: {
        type: 'string',
        description: 'New sales owner',
        required: false,
      },
      closeDate: {
        type: 'string',
        description: 'New expected close date (YYYY-MM-DD)',
        required: false,
      },
    },
  },
  {
    name: 'delete_deal',
    description: 'Delete a deal from the pipeline.',
    category: 'sales',
    requiresConfirmation: true,
    parameters: {
      prospect: {
        type: 'string',
        description: 'Company/prospect name to identify the deal to delete',
        required: true,
      },
    },
  },
];

// ============================================
// TASK TOOLS
// ============================================

const taskTools: AITool[] = [
  {
    name: 'create_task',
    description: 'Create a new task. Use this when the user wants to add a todo item, action item, or task.',
    category: 'tasks',
    requiresConfirmation: false,
    parameters: {
      title: {
        type: 'string',
        description: 'Task title/description',
        required: true,
      },
      domain: {
        type: 'string',
        description: 'Business domain',
        enum: ['EMG', 'PRODUCT', 'GROWTH', 'OPERATIONS', 'FINANCE', 'AIA', 'MAU', 'FUNDRAISING', 'BOARD'],
        required: false,
      },
      owner: {
        type: 'string',
        description: 'Person responsible',
        required: false,
      },
      priority: {
        type: 'string',
        description: 'Priority level',
        enum: ['Critical', 'High', 'Medium', 'Low'],
        required: false,
      },
      targetDate: {
        type: 'string',
        description: 'Target completion date (YYYY-MM-DD)',
        required: false,
      },
      isSignal: {
        type: 'boolean',
        description: 'Whether to mark as a CEO signal',
        required: false,
      },
    },
  },
  {
    name: 'update_task',
    description: 'Update an existing task status, priority, or other fields.',
    category: 'tasks',
    requiresConfirmation: false,
    parameters: {
      taskId: {
        type: 'string',
        description: 'Task ID or title to identify the task',
        required: true,
      },
      status: {
        type: 'string',
        description: 'New status',
        enum: ['Not Started', 'In Progress', 'Done'],
        required: false,
      },
      priority: {
        type: 'string',
        description: 'New priority',
        enum: ['Critical', 'High', 'Medium', 'Low'],
        required: false,
      },
      owner: {
        type: 'string',
        description: 'New owner',
        required: false,
      },
      isSignal: {
        type: 'boolean',
        description: 'Mark as CEO signal',
        required: false,
      },
    },
  },
  {
    name: 'complete_task',
    description: 'Mark a task as complete/done.',
    category: 'tasks',
    requiresConfirmation: false,
    parameters: {
      taskId: {
        type: 'string',
        description: 'Task ID or title to identify the task',
        required: true,
      },
    },
  },
];

// ============================================
// CLIENT TOOLS
// ============================================

const clientTools: AITool[] = [
  {
    name: 'create_client',
    description: 'Add a new client/customer to the system.',
    category: 'clients',
    requiresConfirmation: true,
    parameters: {
      name: {
        type: 'string',
        description: 'Client/company name',
        required: true,
      },
      acv2026: {
        type: 'number',
        description: 'Annual Contract Value for 2026 in EUR',
        required: false,
      },
      health: {
        type: 'string',
        description: 'Client health status',
        enum: ['Healthy', 'At Risk', 'Critical'],
        required: false,
      },
      owner: {
        type: 'string',
        description: 'Account owner',
        required: false,
      },
      industry: {
        type: 'string',
        description: 'Industry vertical',
        required: false,
      },
    },
  },
  {
    name: 'update_client',
    description: 'Update client information like health status or ACV.',
    category: 'clients',
    requiresConfirmation: true,
    parameters: {
      name: {
        type: 'string',
        description: 'Client name to identify',
        required: true,
      },
      acv2026: {
        type: 'number',
        description: 'New ACV for 2026',
        required: false,
      },
      health: {
        type: 'string',
        description: 'New health status',
        enum: ['Healthy', 'At Risk', 'Critical'],
        required: false,
      },
      owner: {
        type: 'string',
        description: 'New account owner',
        required: false,
      },
    },
  },
];

// ============================================
// INVESTOR TOOLS
// ============================================

const investorTools: AITool[] = [
  {
    name: 'create_investor',
    description: 'Add a new investor to the fundraising pipeline.',
    category: 'investors',
    requiresConfirmation: true,
    parameters: {
      name: {
        type: 'string',
        description: 'Investor or contact name',
        required: true,
      },
      firm: {
        type: 'string',
        description: 'VC firm name',
        required: false,
      },
      stage: {
        type: 'string',
        description: 'Pipeline stage',
        enum: ['Lead', 'Research', 'Outreach', 'Meeting', 'Due Diligence', 'Term Sheet', 'Closed', 'Passed'],
        required: false,
      },
      amount: {
        type: 'number',
        description: 'Potential investment amount in EUR',
        required: false,
      },
      email: {
        type: 'string',
        description: 'Contact email',
        required: false,
      },
    },
  },
  {
    name: 'update_investor',
    description: 'Update investor stage or details.',
    category: 'investors',
    requiresConfirmation: true,
    parameters: {
      name: {
        type: 'string',
        description: 'Investor name to identify',
        required: true,
      },
      stage: {
        type: 'string',
        description: 'New pipeline stage',
        enum: ['Lead', 'Research', 'Outreach', 'Meeting', 'Due Diligence', 'Term Sheet', 'Closed', 'Passed'],
        required: false,
      },
      amount: {
        type: 'number',
        description: 'New potential amount',
        required: false,
      },
      notes: {
        type: 'string',
        description: 'Notes to add',
        required: false,
      },
    },
  },
];

// ============================================
// EXPORT ALL TOOLS
// ============================================

export const AI_TOOLS: AITool[] = [
  ...financeTools,
  ...salesTools,
  ...taskTools,
  ...clientTools,
  ...investorTools,
];

/**
 * Get tools by category
 */
export const getToolsByCategory = (category: AITool['category']): AITool[] => {
  return AI_TOOLS.filter(t => t.category === category);
};

/**
 * Get tool by name
 */
export const getToolByName = (name: string): AITool | undefined => {
  return AI_TOOLS.find(t => t.name === name);
};

/**
 * Convert tools to OpenAI function format
 */
export const getOpenAITools = () => {
  return AI_TOOLS.map(tool => ({
    type: 'function' as const,
    function: {
      name: tool.name,
      description: tool.description,
      parameters: {
        type: 'object',
        properties: Object.fromEntries(
          Object.entries(tool.parameters).map(([key, param]) => [
            key,
            {
              type: param.type,
              description: param.description,
              ...(param.enum ? { enum: param.enum } : {}),
            },
          ])
        ),
        required: Object.entries(tool.parameters)
          .filter(([, param]) => param.required)
          .map(([key]) => key),
      },
    },
  }));
};
